import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { merge, of } from 'rxjs';
import { catchError, concatMap, switchMap, takeUntil } from 'rxjs/operators';
import { logoutFailure, logoutSuccess } from '@qvs/core/auth';
import { DateUtils } from '~/shared/utils/date.utils';

import * as fromActions from './slice';
import { selectEDocumentsCurrentBeginDate } from './slice';
import { EDocumentsService } from './e-documents.service';

@Injectable()
export class EDocumentsEffects {
  loadEDocumentsCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadEDocumentsCategories),
      concatMap(() =>
        this.eDocumentsService.loadEDocumentsCategories().pipe(
          switchMap((eDocumentsCategories) => of(fromActions.loadEDocumentsCategoriesSuccess({ eDocumentsCategories }))),
          catchError(() => of(fromActions.loadEDocumentsCategoriesFailure()))
        )
      )
    )
  );

  loadAllEDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadAllEDocuments),
      switchMap(() => of(fromActions.resetEDocumentsState(), fromActions.loadEDocumentsInBatches({ isFirstBatch: true })))
    )
  );

  loadEDocumentsInBatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadEDocumentsInBatches),
      concatLatestFrom(() => [this.store.select(selectEDocumentsCurrentBeginDate)]),
      switchMap(([action, currentBeginDate]) => {
        const isFirstBatch = action.payload?.isFirstBatch;
        const previousBeginDate = currentBeginDate ?? DateUtils.todayFormatted();
        const limitBeginDate = DateUtils.getStartOfXYearsAgo(3);

        if (!DateUtils.isFirstDateBeforeSecondDate(limitBeginDate, previousBeginDate)) {
          return of(fromActions.loadEDocumentsInBatchesSuccess(), fromActions.loadAllEDocumentsSuccess());
        }

        const newBeginDate = EDocumentsEffects.calculateNewBeginDate(previousBeginDate, limitBeginDate, isFirstBatch);

        return this.eDocumentsService.loadEDocumentsForTimePeriod(newBeginDate, previousBeginDate).pipe(
          takeUntil(this.cancelOnRefreshOrLogout$()),
          switchMap((eDocuments) =>
            of(
              ...(isFirstBatch ? [fromActions.loadEDocumentsFirstBatchSuccess()] : []),
              fromActions.setEDocumentsCurrentBeginDate({ currentBeginDate: newBeginDate }),
              fromActions.loadEDocumentsInBatchesSuccess(),
              fromActions.upsertEDocuments({ entities: eDocuments }),
              fromActions.loadEDocumentsInBatches({ isFirstBatch: false })
            )
          ),
          catchError(() => of(fromActions.loadEDocumentsInBatchesFailure()))
        );
      })
    )
  );

  loadEDocumentsMediaAsJson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadEDocumentsMediaAsJson),
      concatMap(({ documentId }) =>
        this.eDocumentsService.loadEDocumentsMediaAsJson(documentId).pipe(
          switchMap((eDocumentsMedia) => of(fromActions.loadEDocumentsMediaAsJsonSuccess({ documentId, eDocumentsMedia }))),
          catchError(() => of(fromActions.loadEDocumentsMediaAsJsonFailure()))
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly eDocumentsService: EDocumentsService,
    private readonly store: Store
  ) {}

  private cancelOnRefreshOrLogout$() {
    return merge(
      this.actions$.pipe(ofType(fromActions.loadAllEDocuments)),
      this.actions$.pipe(ofType(logoutSuccess, logoutFailure))
    );
  }

  static calculateNewBeginDate(currentBeginDate: string, limitBeginDate: string, isFirstBatch: boolean): string {
    const numberOfMonthsToSubtract = isFirstBatch ? 6 : 4 * 12;
    let newBeginDate = DateUtils.subtractMonthsFromDate(currentBeginDate, numberOfMonthsToSubtract);
    if (DateUtils.isFirstDateBeforeSecondDate(newBeginDate, limitBeginDate)) {
      newBeginDate = limitBeginDate;
    }
    return newBeginDate;
  }
}
